/* ═══════════════════════════════════════════════════════════════
   PRO DAY TRADING SUITE — Shell

   This file is intentionally thin. Each of the 6 tabs lives in its
   own component file under /day-trading/, so changes to one tab
   cannot break another.

   ── Phase 10 (2026-02): Trading Suite narrowed to 6 tabs.
   Component files for the removed tabs (MorningPrep, Analyser,
   Scanner, Vault, GrahamValueScreener, WeeklyReview, TradePlan,
   ETFAnalyser, Macro) remain on disk — only their registration here
   was removed so the change is fully reversible. Journal was demoted
   from a top-level tab to a sub-tab inside Options (see Options.jsx).

   Layout responsibilities owned here:
     - PriceProvider (live Yahoo Finance prices, 30s auto-refresh)
     - TickerTape header strip
     - SessionBanner (auto-fired alerts at 9:30 / 11:00 / 15:00 / 15:55 ET)
     - MarketStatus pill
     - Tab switcher
     - Vault auto-scan hook (runs calcStrategies on Vault tickers every 5 min)
     - ErrorBoundary wrapping the whole shell so a crash never blanks the app
═══════════════════════════════════════════════════════════════ */
import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { C } from "./day-trading/_shared";
import { PriceProvider, MarketStatus, TickerTape, FeedStatusBanner, usePrices } from "./day-trading/_prices";
import { WorldClockStrip } from "./day-trading/_worldClock";
import { calcStrategies } from "./day-trading/StrategyAnalyser";
import ErrorBoundary from "../components/ErrorBoundary";

import MarketWatchTab     from "./day-trading/MarketWatch";
import RiskToolsTab       from "./day-trading/RiskTools";
import OptionsTab         from "./day-trading/Options";
import PortfolioTab       from "./day-trading/Portfolio";
import EarningsEdgeTab    from "./day-trading/EarningsEdge";
import AIAnalysisTab      from "./day-trading/AIAnalysis";

// 6 tabs — exact order per the cleanup spec.
const TABS = [
  {label:"Market Watch",  slug:"market-watch",  icon:"📡", Component: MarketWatchTab },
  {label:"Earnings Edge", slug:"earnings-edge", icon:"📅", Component: EarningsEdgeTab},
  {label:"Options",       slug:"options",       icon:"🎯", Component: OptionsTab     },
  {label:"Risk Tools",    slug:"risk-tools",    icon:"⚖️", Component: RiskToolsTab   },
  {label:"Portfolio",     slug:"portfolio",     icon:"💼", Component: PortfolioTab   },
  {label:"AI Analysis",   slug:"ai-analysis",   icon:"🤖", Component: AIAnalysisTab  },
];
const SLUG_TO_INDEX = TABS.reduce((acc, t, i) => { acc[t.slug] = i; return acc; }, {});
// Default landing tab is Options (per spec). Used both when no slug is
// provided and when the slug points to a tab that no longer exists
// (e.g. legacy /day-trading/scanner deep-links → redirect to Options).
const DEFAULT_TAB_INDEX = SLUG_TO_INDEX["options"];

// ── Session banner: auto-fires at 9:30 / 11:00 / 15:00 / 15:55 ET ──
function getETHMM() {
  const etStr = new Date().toLocaleString("en-US", { timeZone: "America/New_York", hour:"2-digit", minute:"2-digit", second:"2-digit", hour12:false });
  const [h, m] = etStr.split(":");
  return { hh: parseInt(h, 10), mm: parseInt(m, 10) };
}
function isMarketOpenWindow() {
  const { hh, mm } = getETHMM();
  const t = hh*60 + mm;
  return (t >= 570 && t < 660) || (t >= 900 && t < 960);
}

const SESSION_BANNERS = [
  { mins: 570, color: C.green, label: "🟢 Market Open — Begin trading", urgent: false },
  { mins: 660, color: C.amber, label: "⚠ Open session ended — stop trading", urgent: false },
  { mins: 900, color: C.green, label: "🟢 Power Hour — Final session begins", urgent: false },
  { mins: 955, color: C.red,   label: "🔴 Close all positions NOW — 5 minutes to close", urgent:true },
];

function SessionBanner() {
  const [active, setActive] = useState(null);
  const lastFiredRef = useRef(0);

  useEffect(() => {
    const tick = () => {
      const { hh, mm } = getETHMM();
      const now = hh*60 + mm;
      const match = SESSION_BANNERS.find(b => b.mins === now);
      if (match && lastFiredRef.current !== match.mins) {
        lastFiredRef.current = match.mins;
        setActive(match);
        setTimeout(() => setActive(null), 60000);
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, []);

  if (!active) return null;
  return (
    <div data-testid="session-banner" style={{
      background: active.color, color: "#fff", padding:"10px 16px",
      fontSize:14, fontWeight:800, letterSpacing:"0.02em",
      display:"flex", alignItems:"center", justifyContent:"space-between", gap:12,
      animation: "slideDown 0.3s ease",
    }}>
      <span>{active.label}</span>
      <button onClick={()=>setActive(null)} style={{ background:"rgba(255,255,255,0.2)", border:"none", color:"#fff", padding:"3px 9px", borderRadius:5, fontSize:11, cursor:"pointer" }}>×</button>
      <style>{`@keyframes slideDown { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

// Vault auto-scan: every 5 min during market windows, count Vault stocks with 2+ active strategies.
function useVaultAutoScan(setAlertCount) {
  const { prices } = usePrices();
  const [vaultTickers, setVaultTickers] = useState([]);

  // Refresh vault tickers from API every 5 min — keeps the auto-scan list in sync
  // with edits the user makes in the Vault tab without a page reload.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || "";
    const fetchVault = async () => {
      try {
        const res = await fetch(`${API}/api/trading/vault`, { credentials: "include" });
        if (!res.ok) return;
        const list = await res.json();
        const ticks = (Array.isArray(list) ? list : []).map(s => s.ticker).filter(Boolean);
        setVaultTickers(ticks);
      } catch { /* noop */ }
    };
    fetchVault();
    const id = setInterval(fetchVault, 5*60*1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const scan = () => {
      if (!isMarketOpenWindow()) { setAlertCount(0); return; }
      // Fall back to a small default set if user hasn't built a vault yet — the
      // auto-scan still surfaces opportunities so the badge isn't permanently 0.
      const tickers = vaultTickers.length > 0
        ? vaultTickers
        : ["NVDA","AAPL","TSLA","MSFT","META","AMD","COIN","PLTR"];
      const hot = [];
      tickers.forEach(t => {
        const d = prices[t];
        if (!d?.ok) return;
        try {
          const fits = calcStrategies(d).filter(s => s.ok);
          if (fits.length >= 2) hot.push({ ticker: t, count: fits.length });
        } catch { /* noop */ }
      });
      setAlertCount(hot.length);
      if (hot.length > 0) {
        const top = hot[0];
        if (typeof window !== "undefined" && document.visibilityState === "visible") {
          document.title = `⚡ ${top.ticker} +${top.count} setups · The Vault`;
          setTimeout(()=>{ document.title = "The Vault"; }, 8000);
        }
      }
    };
    scan();
    const id = setInterval(scan, 5*60*1000);
    return () => clearInterval(id);
  }, [prices, vaultTickers, setAlertCount]);
}

export default function DayTradingPage({ onBackToVault }) {
  // Deep-link aware tab state. URL `/day-trading/:tab` is read on mount
  // and on browser navigation; clicking a tab pushes a new URL so users
  // can copy/share links straight into Options / AI Analysis / etc.
  // Legacy slugs (morning-prep, scanner, journal, vault, value, etc.)
  // that no longer have a tab silently redirect to the default Options tab.
  const params = useParams();
  const navigate = useNavigate();
  const initialIdx = (params?.tab && SLUG_TO_INDEX[params.tab] != null) ? SLUG_TO_INDEX[params.tab] : DEFAULT_TAB_INDEX;
  const [tab, setTabState] = useState(initialIdx);

  // Sync URL when the active tab changes via clicks/programmatic.
  const setTab = (idx) => {
    setTabState(idx);
    const slug = TABS[idx]?.slug;
    if (slug && window.location.pathname !== `/day-trading/${slug}`) {
      navigate(`/day-trading/${slug}`, { replace: false });
    }
  };

  // Sync state when the URL changes externally (back/forward, paste).
  useEffect(() => {
    if (params?.tab && SLUG_TO_INDEX[params.tab] != null && SLUG_TO_INDEX[params.tab] !== tab) {
      setTabState(SLUG_TO_INDEX[params.tab]);
    } else if (params?.tab && SLUG_TO_INDEX[params.tab] == null) {
      // Removed-tab slug — redirect to the default Options tab.
      navigate(`/day-trading/${TABS[DEFAULT_TAB_INDEX].slug}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.tab]);

  const [analyserAlerts,  setAnalyserAlerts]  = useState(0);
  const [strongBuyCount,  setStrongBuyCount]  = useState(0);
  // Cross-tab navigation: when user clicks a ticker in MarketWatch / Portfolio,
  // we lift the request here. The Analyser tab is no longer in the nav, so
  // the request is routed to AI Analysis as the closest analog (per-ticker
  // analysis). The receiving tab can read analyseRequest off props later
  // if/when the AI Analysis prefill flow is wired in.
  const [analyseRequest, setAnalyseRequest] = useState(null); // { ticker, sourceTab }
  // Journal pre-fill: right-click "Log a trade" pushes the ticker here, then
  // Journal (now a sub-tab inside Options) reads + clears it on mount.
  const [journalPrefill, setJournalPrefill] = useState(null);

  const TAB_INDEX = SLUG_TO_INDEX;

  // Called from MarketWatch / Portfolio ticker clicks.
  const requestAnalyse = (ticker) => {
    setAnalyseRequest({ ticker: String(ticker || "").toUpperCase(), sourceTab: tab });
    setTab(TAB_INDEX["ai-analysis"] ?? DEFAULT_TAB_INDEX);
  };
  const requestLogTrade = (payload) => {
    // Accept either a bare ticker string (legacy) or a richer object
    // from Trade Plan: { ticker, entry, stop, target1, target2, shares,
    // maxRisk, maxGain, rr, rationale }.
    const prefill = typeof payload === "string"
      ? { ticker: payload.toUpperCase() }
      : { ...payload, ticker: String(payload?.ticker || "").toUpperCase() };
    setJournalPrefill(prefill);
    // Journal is now a sub-tab inside Options. Deep-link to the journal
    // sub-route so Options.jsx's useSubTab picks it up.
    setTabState(TAB_INDEX["options"] ?? DEFAULT_TAB_INDEX);
    navigate(`/day-trading/options/journal`, { replace: false });
  };

  return (
    <ErrorBoundary label="Day Trading Suite">
      <PriceProvider>
        <DayTradingShell
          tab={tab} setTab={setTab}
          analyserAlerts={analyserAlerts} setAnalyserAlerts={setAnalyserAlerts}
          strongBuyCount={strongBuyCount} setStrongBuyCount={setStrongBuyCount}
          analyseRequest={analyseRequest} clearAnalyseRequest={() => setAnalyseRequest(null)}
          requestAnalyse={requestAnalyse}
          requestLogTrade={requestLogTrade}
          journalPrefill={journalPrefill} clearJournalPrefill={() => setJournalPrefill(null)}
          onBackToVault={onBackToVault}
        />
      </PriceProvider>
    </ErrorBoundary>
  );
}

function DayTradingShell({ tab, setTab, analyserAlerts, setAnalyserAlerts, strongBuyCount, setStrongBuyCount,
                          analyseRequest, clearAnalyseRequest, requestAnalyse, requestLogTrade,
                          journalPrefill, clearJournalPrefill, onBackToVault }) {
  useVaultAutoScan(setAnalyserAlerts);
  const ActiveTab = TABS[tab].Component;
  // Each tab gets only the props it needs.
  let tabProps = {};
  if (TABS[tab].label === "Market Watch") {
    tabProps = { requestAnalyse, requestLogTrade };
  } else if (TABS[tab].label === "Options") {
    // Journal lives inside Options as a sub-tab — forward the prefill
    // and clear-handler so a "Log Trade" click from elsewhere lands in
    // the right place.
    tabProps = { journalPrefill, clearJournalPrefill };
  } else if (TABS[tab].label === "Portfolio") {
    tabProps = { requestAnalyse };
  }
  return (
    <div style={{fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",background:"#f8fafc",minHeight:"100vh",paddingBottom:48}}>
      <FeedStatusBanner/>
      <SessionBanner/>
      <TickerTape/>
      <div style={{background:C.navy,paddingBottom:0}}>
        <div style={{padding:"14px 20px 0"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
              {typeof onBackToVault === "function" && (
                <button
                  onClick={onBackToVault}
                  data-testid="back-to-vault-btn"
                  style={{
                    background:"rgba(241,245,249,0.08)",
                    border:"1px solid rgba(241,245,249,0.18)",
                    color:"#e2e8f0",
                    fontSize:11,
                    fontWeight:600,
                    padding:"6px 12px",
                    borderRadius:7,
                    cursor:"pointer",
                    display:"inline-flex",
                    alignItems:"center",
                    gap:6,
                    transition:"all 0.15s",
                    whiteSpace:"nowrap",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(241,245,249,0.14)"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(241,245,249,0.08)"; e.currentTarget.style.color = "#e2e8f0"; }}
                >
                  ← Back to The Vault
                </button>
              )}
              <div>
                <div style={{fontSize:10,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:3}}>NYSE · NASDAQ · 5-Min Day Trading</div>
                <div style={{fontSize:20,fontWeight:900,color:"#f1f5f9",letterSpacing:"-0.02em"}}>Pro Trading Suite</div>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <MarketStatus/>
              <WorldClockStrip/>
            </div>
          </div>
          <div style={{display:"flex",gap:0,overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
            {TABS.map((t,i) => (
              <button
                key={i}
                onClick={()=>setTab(i)}
                data-testid={`day-trading-tab-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                style={{padding:"8px 14px",border:"none",background:"transparent",cursor:"pointer",fontSize:11,fontWeight:500,color:tab===i?"#f1f5f9":C.dim,borderBottom:tab===i?`2px solid ${C.indigo}`:"2px solid transparent",borderRadius:"6px 6px 0 0",transition:"all 0.15s",whiteSpace:"nowrap",position:"relative"}}
              >
                <span style={{marginRight:4}}>{t.icon}</span>{t.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="dt-tab-body" style={{padding:"18px 24px"}}>
        <ErrorBoundary key={tab} label={TABS[tab].label}>
          <ActiveTab {...tabProps}/>
        </ErrorBoundary>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .dt-tab-body { padding: 12px 10px !important; }
          /* Phase 4 polish: collapse common 2-col / 3-col layouts on
             tabs that don't ship their own media queries (MarketWatch,
             Options, Portfolio, Scanner, etc.). This is a defensive
             catch-all — tabs that already have responsive grids are
             unaffected because their inline styles take precedence
             via the !important markers below only when they declare
             an explicit grid-template-columns. */
          .dt-tab-body [style*="grid-template-columns: 1fr 1fr"],
          .dt-tab-body [style*="grid-template-columns:1fr 1fr"],
          .dt-tab-body [style*="grid-template-columns: 1fr 1fr 1fr"],
          .dt-tab-body [style*="grid-template-columns:1fr 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
          .dt-tab-body table {
            font-size: 11px !important;
          }
        }
        @media (max-width: 480px) {
          .dt-tab-body { padding: 10px 8px !important; }
        }
      `}</style>
    </div>
  );
}
